import React, { useRef, useState, useEffect } from "react";
import { FaStar } from "react-icons/fa";
import { ChevronLeft, ChevronRight } from "lucide-react";

const TestimonialsCarousel = ({ color }) => {

  const testimonials = [
  {
    name: "María Fernanda R.",
    comment:
      "¡Increíble sabor y textura! Las gomitas de vinagre de manzana se han convertido en parte de mi rutina diaria. Me encanta que sean naturales y sin azúcar añadida.",
    rating: 5,
  },
  {
    name: "Carlos Q.",
    comment:
      "La creatina AdAstra me ha ayudado muchísimo en mis entrenamientos. Se disuelve perfecto y siento una mejora real en mi rendimiento.",
    rating: 5,
  },
  {
    name: "Andrea Solís",
    comment:
      "Las proteínas tienen un sabor limpio y se mezclan súper bien con avena o batidos. Es un producto de altísima calidad, ¡totalmente recomendado!",
    rating: 5,
  },
  {
    name: "José Miguel M.",
    comment:
      "Noté los resultados después de dos semanas usando el colágeno con biotina. Piel más firme, cabello más fuerte. ¡Me encantó!",
    rating: 5,
  },
  {
    name: "Valeria C.",
    comment:
      "Excelente presentación y empaque. Se nota el cuidado que ponen en cada detalle, desde el diseño hasta la calidad del producto.",
    rating: 5,
  },
];

// === TESTIMONIALS AUTO-SCROLL (idéntico a FeaturedProducts) ===
const [activeCenterIndex, setActiveCenterIndex] = useState(0);
const [hoveredIndex, setHoveredIndex] = useState(null);
const scrollRef = useRef(null);
const animationRef = useRef(null);
const pausedByHover = useRef(false);
const pausedForFocus = useRef(false);
const lastFocusedIndex = useRef(null);
const scrollPausedTimeoutRef = useRef(null);
const isScrolling = useRef(false);

const duplicatedTestimonials = [...testimonials, ...testimonials, ...testimonials];

const scrollStepTestimonials = () => {
  const container = scrollRef.current;
  if (!container || pausedByHover.current || pausedForFocus.current) return;

  container.scrollLeft += 0.5;

  // Reset suave al llegar al final del duplicado
  if (container.scrollLeft >= container.scrollWidth / 1.5) {
    container.scrollLeft = container.scrollWidth / 3 - container.offsetWidth;
  }

  const children = Array.from(container.children);
  let closestIndex = 0;
  let closestDistance = Infinity;

  children.forEach((child, index) => {
    const rect = child.getBoundingClientRect();
    const childCenter = rect.left + rect.width / 2;
    const containerRect = container.getBoundingClientRect();
    const distance = Math.abs(
      childCenter - (containerRect.left + container.offsetWidth / 2)
    );
    if (distance < closestDistance) {
      closestDistance = distance;
      closestIndex = index;
    }
  });

  setActiveCenterIndex(closestIndex % testimonials.length);
  animationRef.current = requestAnimationFrame(scrollStepTestimonials);
};

const startAutoScroll = () => {
  cancelAnimationFrame(animationRef.current);
  animationRef.current = requestAnimationFrame(scrollStepTestimonials);
};

const stopAutoScroll = () => {
  cancelAnimationFrame(animationRef.current);
};

useEffect(() => {
  startAutoScroll();
  return () => stopAutoScroll();  
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [testimonials]);

const handleMouseEnter = (i) => {
  setHoveredIndex(i);
  pausedByHover.current = true;
  stopAutoScroll();
};

const handleMouseLeave = () => {
  setHoveredIndex(null);
  pausedByHover.current = false;
  startAutoScroll();
};

const pauseAutoScrollTemporarily = (delay = 7000) => {
  stopAutoScroll();
  pausedForFocus.current = true;
  if (scrollPausedTimeoutRef.current) clearTimeout(scrollPausedTimeoutRef.current);

  scrollPausedTimeoutRef.current = setTimeout(() => {
    pausedForFocus.current = false;
    startAutoScroll();
  }, delay);
};

const scrollToTestimonialIndex = (realIndex) => {
  const container = scrollRef.current;
  if (!container || isScrolling.current) return;

  isScrolling.current = true;
  stopAutoScroll();

  const children = Array.from(container.children);
  const containerScroll = container.scrollLeft + container.offsetWidth / 2;

  const matching = children
    .map((child, i) => ({ child, i, logicalIndex: i % testimonials.length }))
    .filter((item) => item.logicalIndex === realIndex);

  const closest = matching.reduce((prev, curr) => {
    const prevOffset = Math.abs(prev.child.offsetLeft - containerScroll);
    const currOffset = Math.abs(curr.child.offsetLeft - containerScroll);
    return currOffset < prevOffset ? curr : prev;
  });

  if (!closest) {
    isScrolling.current = false;
    return;
  }

  const offset =
    closest.child.offsetLeft +
    closest.child.offsetWidth / 2 -
    container.offsetWidth / 2;

  container.scrollTo({ left: offset, behavior: "smooth" });
  setActiveCenterIndex(realIndex);
  lastFocusedIndex.current = realIndex;
  pauseAutoScrollTemporarily();

  setTimeout(() => {
    isScrolling.current = false;
  }, 600);
};

const handleDotClick = (index) => scrollToTestimonialIndex(index);

  return (
    <section
      id="testimonials"
      className="w-full py-16 relative bg-white text-gray-900"
    >
      <div className="max-w-7xl mx-auto px-6">
        <h2 className="text-3xl sm:text-4xl font-extrabold mb-12 leading-snug text-center">
          Ingredientes de calidad crean{" "}
          <span
            className="relative inline-block"
            style={{ color: color }}
          >
            clientes felices.
            <span
              className="absolute bottom-0 left-0 w-full h-2 -z-10 rounded"
              style={{ backgroundColor: `${color}40` }}
            ></span>
          </span>
        </h2>

        {/* === Carrusel === */}
        <div
          ref={scrollRef}
          className="flex overflow-x-auto overflow-y-hidden no-scrollbar gap-4 py-4 scroll-smooth w-screen max-w-full"
          style={{
            scrollBehavior: "auto",
            whiteSpace: "nowrap",
            WebkitOverflowScrolling: "touch",
          }}
        >
          {duplicatedTestimonials.map((t, index) => {
            const realIndex = index % testimonials.length;
            const isHovered = hoveredIndex === realIndex;
            const isCenter = realIndex === activeCenterIndex;
            const isActive = hoveredIndex !== null ? isHovered : isCenter;

            return (
              <div
                key={`${realIndex}-${index}`}
                onMouseEnter={() => handleMouseEnter(realIndex)}
                onMouseLeave={handleMouseLeave}
                className={`flex-shrink-0 cursor-pointer group transition-transform duration-500 px-3
                  ${isActive ? "scale-105 opacity-100 z-10" : "opacity-60"}
                `}
                style={{
                  width: "clamp(250px, 70vw, 340px)",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <div
                  className={`w-full bg-[#f3f3f3] rounded-xl p-6 flex flex-col justify-between text-center transition duration-300 ${
                    isActive ? "shadow-xl" : "shadow-sm"
                  }`}
                  style={{
                    minHeight: "300px",
                    height: "300px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    overflow: "hidden",
                    wordBreak: "break-word",
                    whiteSpace: "normal",
                  }}
                >
                  <p className="text-sm sm:text-base leading-relaxed font-semibold mb-4 text-gray-800 line-clamp-6">
                    “{t.comment}”
                  </p>

                  <div className="flex justify-center mb-3 flex-wrap">
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <FaStar
                        key={i}
                        className="text-yellow-400 text-lg mx-[1px]"
                      />
                    ))}
                  </div>

                  <h4 className="font-semibold text-sm text-gray-700">
                    - {t.name}
                  </h4>
                </div>
              </div>
            );
          })}
        </div>

        {/* === Controles === */}
        <div className="flex justify-center items-center gap-3 mt-8">
          <button
            onClick={() =>
              scrollToTestimonialIndex(
                (activeCenterIndex - 1 + testimonials.length) %
                  testimonials.length
              )
            }
            className="p-2 rounded-full border border-gray-300 bg-white shadow hover:bg-black transition group"
          >
            <ChevronLeft
              size={20}
              className="text-black group-hover:text-white transition-colors"
            />
          </button>

          {testimonials.map((_, index) => (
            <button
              key={index}
              onClick={() => handleDotClick(index)}
              className={`w-3 h-3 rounded-full transition ${
                activeCenterIndex === index ? "bg-black" : "bg-gray-300"
              }`}
            />
          ))}

          <button
            onClick={() =>
              scrollToTestimonialIndex((activeCenterIndex + 1) % testimonials.length)
            }
            className="p-2 rounded-full border border-gray-300 bg-white shadow hover:bg-black transition group"
          >
            <ChevronRight
              size={20}
              className="text-black group-hover:text-white transition-colors"
            />
          </button>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsCarousel;
